import { SenderWarningBanner } from './SenderWarning';
// src/components/Settings.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import { getStoredUser } from '../utils/authUser';
import { getAnalyticsDashboard } from '../services/analyticsDashboardService';
import PrivateListingForm from './PrivateListingForm';
import './Settings.css';

const MISC_IMAGES_API = 'https://tophamtechbeb.com/api/toptech/onboarding/v1/misc-images';
const MISC_IMAGES_API_KEY = 'treas-api-key-2025';
const MAX_MISC_IMAGES = 10;
const SITE_MODIFY_REQUEST_TYPE_MAP = {
  page_copy_change: 'page_copy_change',
  image_change: 'page_refactor',
  agent_about_change: 'agent_about_change',
  add_agent: 'add_agent',
  remove_agent: 'delete_agent',
  new_page: 'feature_request',
  page_refactor: 'page_refactor',
  feature_request: 'feature_request',
  other: 'feature_request'
};

const ANALYTICS_RANGE_LABELS = {
  '7d': '7 days',
  '30d': '30 days',
  '120d': '120 days'
};

const formatAnalyticsNumber = (value) => Number(value || 0).toLocaleString('en-US');

const AnalyticsRanking = ({ title, rows, labelKey, valueKey, emptyMessage, secondaryKey }) => (
  <section className="analytics-ranking" aria-label={title}>
    <h4>{title}</h4>
    {rows?.length ? (
      <ol className="analytics-ranking-list">
        {rows.map((row, index) => (
          <li key={`${row[labelKey]}-${index}`}>
            <span className="analytics-ranking-position">{index + 1}</span>
            <span className="analytics-ranking-label" title={row[labelKey]}>{row[labelKey]}</span>
            {secondaryKey && (
              <span className="analytics-ranking-secondary">
                {formatAnalyticsNumber(row[secondaryKey])} users
              </span>
            )}
            <strong>{formatAnalyticsNumber(row[valueKey])}</strong>
          </li>
        ))}
      </ol>
    ) : (
      <p className="analytics-empty-list">{emptyMessage}</p>
    )}
  </section>
);

const Settings = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [contactValue, setContactValue] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelText, setCancelText] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [pageViews, setPageViews] = useState(null);
  const [analyticsDashboard, setAnalyticsDashboard] = useState(null);
  const [analyticsRange, setAnalyticsRange] = useState('30d');
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState('');
  const [analyticsRefreshKey, setAnalyticsRefreshKey] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [issueType, setIssueType] = useState('Site Issue');
  const [issueDescription, setIssueDescription] = useState('');
  const [showIssueSuccessModal, setShowIssueSuccessModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyType, setModifyType] = useState('page_copy_change');
  const [modifyDescription, setModifyDescription] = useState('');
  const [modifyTargetPage, setModifyTargetPage] = useState('');
  const [modifyTargetAgent, setModifyTargetAgent] = useState('');
  const [modifyPriority, setModifyPriority] = useState('medium');
  const [miscImages, setMiscImages] = useState([]);
  const [miscImagesLoading, setMiscImagesLoading] = useState(false);
  const [miscImagesUploading, setMiscImagesUploading] = useState(false);
  const [miscImageDeleting, setMiscImageDeleting] = useState('');
  const [miscImagesError, setMiscImagesError] = useState('');
  const [miscImagesLoadFailed, setMiscImagesLoadFailed] = useState(false);
  const [showModifySuccessModal, setShowModifySuccessModal] = useState(false);
  const [showPrivateListingForm, setShowPrivateListingForm] = useState(false);
  const [showListingSuccessModal, setShowListingSuccessModal] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
    fetchPageViews();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchAnalyticsDashboard = async () => {
      setAnalyticsLoading(true);
      setAnalyticsError('');

      try {
        const data = await getAnalyticsDashboard({
          user: getStoredUser(),
          range: analyticsRange,
          signal: controller.signal
        });
        setAnalyticsDashboard(data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching Google Analytics dashboard:', error);
          setAnalyticsError(error.message || 'Analytics are temporarily unavailable.');
        }
      } finally {
        if (!controller.signal.aborted) setAnalyticsLoading(false);
      }
    };

    fetchAnalyticsDashboard();
    return () => controller.abort();
  }, [analyticsRange, analyticsRefreshKey]);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?siteId=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setEmailValue(data.agentEmail || '');
        setContactValue(data.agentContact || '');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/comm/templates?siteId=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleChatLeadTemplateSelect = async (templateName) => {
    await updateSettings({ chatleadEmailTemplate: templateName });
  };

  const handleSignupTemplateSelect = async (templateName) => {
    await updateSettings({ signupEmailTemplate: templateName });
  };

  const fetchPageViews = async () => {
    try {
      const response = await fetch(`https://tophamtechbeb.com/aw-monitor/v1/page-views?site_id=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPageViews(data);
      }
    } catch (error) {
      console.error('Error fetching page views:', error);
    }
  };

  const updateSettings = async (updatedFields) => {
    try {
      const params = new URLSearchParams({
        siteId: SITE_CONFIG.SITE_ID,
        ...updatedFields
      });
      
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?${params}`, {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        fetchSettings(); // Refresh settings
      }
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const updateEmail = async () => {
    await updateSettings({ agentEmail: emailValue });
    setEditingEmail(false);
  };

  const updateContact = async () => {
    await updateSettings({ agentContact: contactValue });
    setEditingContact(false);
  };

  const getMiscImageUrl = (image) => {
    if (typeof image === 'string') return image;
    return image?.misc_image_url || image?.image_url || image?.imageUrl || image?.url || image?.s3_url || image?.location || image?.path || '';
  };

  const getMiscImageIdentifier = (image) => {
    if (typeof image !== 'string') {
      const identifier = image?.file_id || image?.image_id || image?.imageId || image?.id || image?.key || image?.filename || image?.name;
      if (identifier !== undefined && identifier !== null) return String(identifier);
    }

    const imageUrl = getMiscImageUrl(image);
    if (!imageUrl) return '';
    try {
      return decodeURIComponent(new URL(imageUrl, window.location.origin).pathname.split('/').filter(Boolean).pop() || '');
    } catch (error) {
      return imageUrl.split('/').filter(Boolean).pop()?.split('?')[0] || '';
    }
  };

  const getMiscImageLabel = (image, index) => {
    if (typeof image !== 'string') {
      const label = image?.original_filename
        || image?.originalFilename
        || image?.filename
        || image?.name
        || image?.s3_key?.split('/').filter(Boolean).pop();
      if (label) return label;
    }
    return getMiscImageIdentifier(image) || `Reference image ${index + 1}`;
  };

  const fetchMiscImages = async () => {
    setMiscImagesLoading(true);
    setMiscImagesError('');
    setMiscImagesLoadFailed(false);

    try {
      const response = await fetch(`${MISC_IMAGES_API}/${encodeURIComponent(SITE_CONFIG.SITE_ID)}`, {
        headers: { 'X-API-Key': MISC_IMAGES_API_KEY }
      });

      if (!response.ok) throw new Error(`Image request failed with status ${response.status}`);

      const payload = await response.json();
      const images = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.images)
            ? payload.images
            : [];
      setMiscImages(images);
    } catch (error) {
      console.error('Error fetching site change images:', error);
      setMiscImagesError('We could not load your images. Please try again.');
      setMiscImagesLoadFailed(true);
    } finally {
      setMiscImagesLoading(false);
    }
  };

  const openModifyModal = () => {
    setShowModifyModal(true);
    fetchMiscImages();
  };

  const uploadMiscImages = async (event) => {
    const input = event.target;
    const selectedFiles = Array.from(input.files || []);
    input.value = '';

    if (!selectedFiles.length) return;

    const invalidFile = selectedFiles.find((file) => !['image/jpeg', 'image/png', 'image/webp'].includes(file.type));
    if (invalidFile) {
      setMiscImagesError('Please choose JPG, PNG, or WebP images only.');
      return;
    }

    const availableSlots = Math.max(0, MAX_MISC_IMAGES - miscImages.length);
    if (selectedFiles.length > availableSlots) {
      setMiscImagesError(`You can add ${availableSlots} more image${availableSlots === 1 ? '' : 's'} (10 maximum).`);
      return;
    }

    setMiscImagesUploading(true);
    setMiscImagesError('');

    try {
      const formData = new FormData();
      formData.append('site_id', SITE_CONFIG.SITE_ID);
      if (selectedFiles.length === 1) {
        formData.append('image', selectedFiles[0]);
      } else {
        selectedFiles.forEach((file) => formData.append('images', file));
      }

      const response = await fetch(MISC_IMAGES_API, {
        method: 'POST',
        headers: { 'X-API-Key': MISC_IMAGES_API_KEY },
        body: formData
      });

      if (!response.ok) throw new Error(`Image upload failed with status ${response.status}`);
      await fetchMiscImages();
    } catch (error) {
      console.error('Error uploading site change images:', error);
      setMiscImagesError('We could not upload those images. Please try again.');
    } finally {
      setMiscImagesUploading(false);
    }
  };

  const deleteMiscImage = async (image) => {
    const identifier = getMiscImageIdentifier(image);
    if (!identifier) {
      setMiscImagesError('We could not identify that image. Refresh the gallery and try again.');
      return;
    }

    if (!window.confirm('Delete this image?')) return;

    setMiscImageDeleting(identifier);
    setMiscImagesError('');

    try {
      const response = await fetch(
        `${MISC_IMAGES_API}/${encodeURIComponent(SITE_CONFIG.SITE_ID)}/${encodeURIComponent(identifier)}`,
        {
          method: 'DELETE',
          headers: { 'X-API-Key': MISC_IMAGES_API_KEY }
        }
      );

      if (!response.ok) throw new Error(`Image deletion failed with status ${response.status}`);
      await fetchMiscImages();
    } catch (error) {
      console.error('Error deleting site change image:', error);
      setMiscImagesError('We could not delete that image. Please try again.');
    } finally {
      setMiscImageDeleting('');
    }
  };

  const submitModifyRequest = async () => {
    if (!modifyDescription.trim()) {
      alert('Please describe the change you would like.');
      return;
    }

    try {
      const payload = {
        customer_id: SITE_CONFIG.SITE_ID,
        site_id: SITE_CONFIG.SITE_ID,
        request_type: SITE_MODIFY_REQUEST_TYPE_MAP[modifyType] || 'feature_request',
        description: modifyDescription.trim(),
        priority: modifyPriority,
        metadata: {
          requested_change_type: modifyType,
          reference_image_count: miscImages.length
        }
      };
      if (modifyTargetPage.trim()) payload.target_page = modifyTargetPage.trim();
      if (modifyTargetAgent.trim()) payload.target_agent = modifyTargetAgent.trim();

      const response = await fetch('https://tophamtechbeb.com/aw-monitor/v1/site-modify-req', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowModifyModal(false);
        setModifyType('page_copy_change');
        setModifyDescription('');
        setModifyTargetPage('');
        setModifyTargetAgent('');
        setModifyPriority('medium');
        setShowModifySuccessModal(true);
      } else {
        let validationMessage = '';
        try {
          const errorPayload = await response.json();
          if (Array.isArray(errorPayload?.detail)) {
            validationMessage = errorPayload.detail.map((detail) => detail.msg).filter(Boolean).join(' ');
          } else if (typeof errorPayload?.detail === 'string') {
            validationMessage = errorPayload.detail;
          }
        } catch (parseError) {
          console.error('Error parsing site modify response:', parseError);
        }
        alert(validationMessage || 'Failed to submit the request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting site modify request:', error);
      alert('Failed to submit the request. Please try again.');
    }
  };

  const handleReportIssue = () => {
    setShowReportModal(true);
  };

  const submitIssueReport = async () => {
    if (!issueDescription.trim()) {
      alert('Please provide a description of the issue.');
      return;
    }

    try {
      const response = await fetch('https://tophamtechbeb.com/aw-monitor/v1/incidents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: SITE_CONFIG.SITE_ID,
          incident_type: issueType,
          description: issueDescription,
          incident_date: new Date().toISOString().split('T')[0]
        })
      });
      
      if (response.ok) {
        setShowReportModal(false);
        setIssueType('Site Issue');
        setIssueDescription('');
        setShowIssueSuccessModal(true);
      } else {
        alert('Failed to report issue. Please try again.');
      }
    } catch (error) {
      console.error('Error reporting issue:', error);
      alert('Failed to report issue. Please try again.');
    }
  };

  const handleCancelSubscription = async () => {
    if (cancelText !== 'Cancel Subscription') {
      alert('Please type "Cancel Subscription" exactly as shown.');
      return;
    }

    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/cancel-subscription?siteId=${SITE_CONFIG.SITE_ID}`, {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        setShowCancelModal(false);
        setCancelText('');
        setShowSuccessModal(true);
        fetchSettings(); // Refresh settings
      } else {
        alert('Failed to cancel subscription. Please try again.');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    }
  };

  const chatLeadTemplates = templates.filter((template) => (
    !template.scope || String(template.scope).toLowerCase() === 'brokerage'
  ));

  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }

  return (
    <div className={`settings ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="tools-header">
        <h1>Settings</h1>
        <div className="header-buttons">
          <button onClick={() => navigate('/admin')} className="back-button">
            Back to Admin
          </button>
          <button onClick={openModifyModal} className="report-issue-button">
            Request a Change
          </button>
          <button onClick={handleReportIssue} className="report-issue-button">
            Report an Issue
          </button>
        </div>
      </div>

      <SenderWarningBanner />

      <div className="settings-container">
        <div className="settings-section">
          <h2>Toggle Settings</h2>
          
          <div className="setting-item">
            <div className="setting-info">
              <label>Chat Lead Text</label>
              <p>Enable text notifications for chat leads</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.chatleadText || false}
                onChange={(e) => updateSettings({ chatleadText: e.target.checked })}
              />
              <span className="slider"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Lead Pop-up</label>
              <p>Invite signed-out visitors to request free market insights after 15 seconds</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.leadPopUp || false}
                onChange={(e) => updateSettings({ leadPopUp: e.target.checked })}
                aria-label="Enable lead pop-up"
              />
              <span className="slider"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Open Chat</label>
              <p>Allow users to chat without signing in or selecting a service</p>
              <p style={{color: '#ff9800', fontSize: '12px', marginTop: '5px'}}>Toggling this ON may result in fewer chat leads</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.openChat || false}
                onChange={(e) => updateSettings({ openChat: e.target.checked })}
              />
              <span className="slider"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Chat Lead Follow-up</label>
              <p>Manage the brokerage-wide email sequence for new chat leads</p>
            </div>
            <div className="automation-setting-actions">
              <span className={`automation-setting-status ${settings?.automatedEmailFollowup ? 'active' : 'paused'}`}>
                {settings?.automatedEmailFollowup ? 'Active' : 'Paused'}
              </span>
              <button type="button" onClick={() => navigate('/admin/email-manager?tab=automations')}>
                Manage
              </button>
            </div>
          </div>

          <div className="template-selector-container">
            <div className="template-selector-header">
              <label>Immediate Chat Lead Email Template</label>
              <p>Send this email immediately when a new chat lead is captured</p>
            </div>
            <div className="template-dropdown">
              <select
                value={settings?.chatleadEmailTemplate || ''}
                onChange={(e) => handleChatLeadTemplateSelect(e.target.value)}
                className="template-select"
              >
                <option value="" disabled>Select a template...</option>
                {chatLeadTemplates.map((template) => (
                  <option key={template.id} value={template.templateName}>
                    {template.templateName}
                  </option>
                ))}
              </select>
            </div>
            {settings?.chatleadEmailTemplate && (
              <div className="selected-template">
                <span>Selected: {settings.chatleadEmailTemplate}</span>
              </div>
            )}

            <div className="template-selector-header" style={{marginTop: '20px'}}>
              <label>Signup Email Template</label>
              <p>Select the template to use for new user signup emails</p>
            </div>
            <div className="template-dropdown">
              <select
                value={settings?.signupEmailTemplate || ''}
                onChange={(e) => handleSignupTemplateSelect(e.target.value)}
                className="template-select"
              >
                <option value="">Select a template...</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.templateName}>
                    {template.templateName}
                  </option>
                ))}
              </select>
            </div>
            {settings?.signupEmailTemplate && (
              <div className="selected-template">
                <span>Selected: {settings.signupEmailTemplate}</span>
              </div>
            )}
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Round Robin Lead Assignment</label>
              <p>Automatically assign new leads to agents in rotation</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.roundRobin || false}
                onChange={(e) => updateSettings({ roundRobin: e.target.checked })}
              />
              <span className="slider"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>User Signup Text</label>
              <p>Show signup prompt text to users</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.userSignupText || false}
                onChange={(e) => updateSettings({ userSignupText: e.target.checked })}
              />
              <span className="slider"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Digital Marketing</label>
              <p>Enable digital marketing campaigns</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.digitalMarketing || false}
                disabled
              />
              <span className="slider disabled"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Mail Marketing</label>
              <p>Enable physical mail marketing campaigns</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.mailMarketing || false}
                disabled
              />
              <span className="slider disabled"></span>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Active</label>
              <p>Account active status</p>
            </div>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings?.active || false}
                disabled
              />
              <span className="slider disabled"></span>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h2>Account Information</h2>
          
          <div className="info-item">
            <label>Plan Status</label>
            <div className="info-value">
              <span className="plan-status">{settings?.paid || 'Free'}</span>
            </div>
          </div>

          <div className="info-item">
            <label>Agent Email</label>
            <div className="info-value">
              {editingEmail ? (
                <div className="edit-field">
                  <input
                    type="email"
                    value={emailValue}
                    onChange={(e) => setEmailValue(e.target.value)}
                    className="edit-input"
                  />
                  <button onClick={updateEmail} className="save-btn">✓</button>
                  <button onClick={() => setEditingEmail(false)} className="cancel-btn">✕</button>
                </div>
              ) : (
                <div className="display-field">
                  <span>{settings?.agentEmail || 'Not set'}</span>
                  <button onClick={() => setEditingEmail(true)} className="edit-btn">✏️</button>
                </div>
              )}
            </div>
          </div>

          <div className="info-item">
            <label>Agent Contact</label>
            <div className="info-value">
              {editingContact ? (
                <div className="edit-field">
                  <input
                    type="tel"
                    value={contactValue}
                    onChange={(e) => setContactValue(e.target.value)}
                    className="edit-input"
                  />
                  <button onClick={updateContact} className="save-btn">✓</button>
                  <button onClick={() => setEditingContact(false)} className="cancel-btn">✕</button>
                </div>
              ) : (
                <div className="display-field">
                  <span>{settings?.agentContact || 'Not set'}</span>
                  <button onClick={() => setEditingContact(true)} className="edit-btn">✏️</button>
                </div>
              )}
            </div>
          </div>

          <div className="cancel-section">
            <button 
              onClick={() => setShowCancelModal(true)}
              className="cancel-subscription-btn"
            >
              Cancel Subscription
            </button>
          </div>

        </div>

        <section className="settings-section analytics-dashboard-section" aria-labelledby="site-analytics-title">
          <div className="analytics-dashboard-header">
            <div>
              <span className="analytics-eyebrow">Google Analytics 4</span>
              <h2 id="site-analytics-title">Site Analytics</h2>
              <p>Traffic and engagement for this website. Recent activity may take time to appear.</p>
            </div>
            <div className="analytics-range-control" role="group" aria-label="Analytics date range">
              {Object.entries(ANALYTICS_RANGE_LABELS).map(([range, label]) => (
                <button
                  type="button"
                  key={range}
                  className={analyticsRange === range ? 'active' : ''}
                  aria-pressed={analyticsRange === range}
                  onClick={() => setAnalyticsRange(range)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {analyticsLoading ? (
            <div className="analytics-dashboard-message" role="status">Loading Google Analytics…</div>
          ) : analyticsError ? (
            <div className="analytics-dashboard-message analytics-dashboard-error" role="alert">
              <strong>We couldn't load analytics.</strong>
              <span>{analyticsError}</span>
              <button type="button" onClick={() => setAnalyticsRefreshKey((key) => key + 1)}>Try again</button>
            </div>
          ) : analyticsDashboard?.configured === false ? (
            <div className="analytics-dashboard-message">
              <strong>Analytics is not configured for this site yet.</strong>
              <span>Contact support to connect this site’s Google Analytics property.</span>
            </div>
          ) : analyticsDashboard ? (
            <>
              <div className="analytics-summary-grid">
                {[
                  ['Active users', analyticsDashboard.totals?.activeUsers],
                  ['Page views', analyticsDashboard.totals?.pageViews],
                  ['Searches', analyticsDashboard.totals?.searches],
                  ['Listing views', analyticsDashboard.totals?.listingViews],
                  ['Logins', analyticsDashboard.totals?.logins],
                  ['Sign-ups', analyticsDashboard.totals?.signUps]
                ].map(([label, value]) => (
                  <div className="analytics-summary-card" key={label}>
                    <span>{label}</span>
                    <strong>{formatAnalyticsNumber(value)}</strong>
                  </div>
                ))}
              </div>

              <div className="analytics-audience-card">
                <div>
                  <span className="analytics-audience-label">Audience</span>
                  <strong>Signed in vs. anonymous</strong>
                </div>
                <div className="analytics-audience-values">
                  <span><i className="signed-in" aria-hidden="true" />Signed in <strong>{formatAnalyticsNumber(analyticsDashboard.audience?.signedInUsers)}</strong></span>
                  <span><i className="anonymous" aria-hidden="true" />Anonymous <strong>{formatAnalyticsNumber(analyticsDashboard.audience?.anonymousUsers)}</strong></span>
                </div>
              </div>

              <div className="analytics-rankings-grid">
                <AnalyticsRanking
                  title="Top pages"
                  rows={analyticsDashboard.topPages}
                  labelKey="pagePath"
                  valueKey="views"
                  secondaryKey="activeUsers"
                  emptyMessage="No page data for this range."
                />
                <AnalyticsRanking
                  title="Top searches"
                  rows={analyticsDashboard.topSearches}
                  labelKey="searchTerm"
                  valueKey="searches"
                  emptyMessage="No searches recorded for this range."
                />
                <AnalyticsRanking
                  title="Top listings"
                  rows={analyticsDashboard.topListings}
                  labelKey="listingKey"
                  valueKey="views"
                  emptyMessage="No listing views recorded for this range."
                />
              </div>

              <div className="analytics-dashboard-footer">
                <span>
                  Updated {analyticsDashboard.generatedAt
                    ? new Date(analyticsDashboard.generatedAt).toLocaleString()
                    : 'recently'}
                </span>
                <span>Site {analyticsDashboard.siteId || SITE_CONFIG.SITE_ID}</span>
              </div>
            </>
          ) : null}

          <details className="legacy-analytics-summary">
            <summary>Recent monitoring traffic</summary>
            {pageViews ? (
              <div className="legacy-analytics-stats">
                <span>Page views <strong>{formatAnalyticsNumber(pageViews.count)}</strong></span>
                <span>Mobile visits <strong>{formatAnalyticsNumber(pageViews.page_views?.filter((view) => view.is_mobile).length)}</strong></span>
                <span>Unique sessions <strong>{formatAnalyticsNumber(new Set(pageViews.page_views?.map((view) => view.session_id)).size)}</strong></span>
              </div>
            ) : (
              <span className="legacy-analytics-loading">Loading recent traffic…</span>
            )}
          </details>
        </section>
      </div>

      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel Subscription</h3>
            <p>This action cannot be undone. To confirm, please type "Cancel Subscription" below:</p>
            <input
              type="text"
              value={cancelText}
              onChange={(e) => setCancelText(e.target.value)}
              placeholder="Type: Cancel Subscription"
              className="cancel-input"
            />
            <div className="modal-buttons">
              <button 
                onClick={handleCancelSubscription}
                className="confirm-cancel-btn"
                disabled={cancelText !== 'Cancel Subscription'}
              >
                Confirm Cancellation
              </button>
              <button 
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelText('');
                }}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✓</div>
            <h3>Subscription Cancelled</h3>
            <p>Your subscription has been successfully cancelled. You will retain access until the end of your current billing period.</p>
            <div className="modal-buttons">
              <button 
                onClick={() => setShowSuccessModal(false)}
                className="success-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showModifyModal && (
        <div className="modal-overlay" onClick={() => setShowModifyModal(false)}>
          <div className="modal-content site-change-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Request a Site Change</h3>
            <p>Tell us what you'd like changed on your site:</p>

            <label>Change Type:</label>
            <select
              value={modifyType}
              onChange={(e) => setModifyType(e.target.value)}
              className="issue-select"
            >
              <option value="page_copy_change">Update page text</option>
              <option value="image_change">Swap or update images</option>
              <option value="agent_about_change">Update an agent bio</option>
              <option value="add_agent">Add an agent</option>
              <option value="remove_agent">Remove an agent</option>
              <option value="new_page">Add a new page</option>
              <option value="page_refactor">Redesign a page</option>
              <option value="feature_request">New feature</option>
              <option value="other">Other</option>
            </select>

            <label>Description:</label>
            <textarea
              value={modifyDescription}
              onChange={(e) => setModifyDescription(e.target.value)}
              placeholder="Describe the change - the more detail, the better..."
              className="issue-textarea"
              rows="4"
            />

            <label>Page (optional):</label>
            <input
              value={modifyTargetPage}
              onChange={(e) => setModifyTargetPage(e.target.value)}
              placeholder="e.g. /contact or homepage"
              className="issue-select"
            />

            <label>Agent (optional):</label>
            <input
              value={modifyTargetAgent}
              onChange={(e) => setModifyTargetAgent(e.target.value)}
              placeholder="Agent name, if this is about a team member"
              className="issue-select"
            />

            <label>Priority:</label>
            <select
              value={modifyPriority}
              onChange={(e) => setModifyPriority(e.target.value)}
              className="issue-select"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>

            <section className="site-change-images" aria-labelledby="site-change-images-title">
              <div className="site-change-images-header">
                <div>
                  <h4 id="site-change-images-title">Reference images</h4>
                  <span>JPG, PNG, or WebP</span>
                </div>
                <strong>{miscImages.length} / {MAX_MISC_IMAGES}</strong>
              </div>

              {miscImagesLoading && !miscImages.length ? (
                <div className="site-change-images-status" role="status">Loading your images…</div>
              ) : miscImages.length ? (
                <div className="site-change-image-grid">
                  {miscImages.map((image, index) => {
                    const imageUrl = getMiscImageUrl(image);
                    const identifier = getMiscImageIdentifier(image);
                    const label = getMiscImageLabel(image, index);
                    const isDeleting = miscImageDeleting === identifier;

                    return (
                      <div className="site-change-image" key={identifier || imageUrl || index}>
                        {imageUrl ? (
                          <img src={imageUrl} alt={label} />
                        ) : (
                          <div className="site-change-image-unavailable">Preview unavailable</div>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteMiscImage(image)}
                          disabled={Boolean(miscImageDeleting)}
                          aria-label={`Delete ${label}`}
                        >
                          {isDeleting ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="site-change-images-empty">No reference images added yet.</div>
              )}

              {miscImagesError && (
                <div className="site-change-images-error" role="alert">
                  <span>{miscImagesError}</span>
                  {miscImagesLoadFailed && (
                    <button type="button" onClick={fetchMiscImages}>Retry</button>
                  )}
                </div>
              )}

              <label className={`site-change-image-upload ${miscImages.length >= MAX_MISC_IMAGES || miscImagesUploading || miscImagesLoading || miscImagesLoadFailed ? 'disabled' : ''}`}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={uploadMiscImages}
                  disabled={miscImages.length >= MAX_MISC_IMAGES || miscImagesUploading || miscImagesLoading || miscImagesLoadFailed}
                />
                {miscImagesUploading
                  ? 'Uploading images…'
                  : miscImagesLoading
                    ? 'Loading images…'
                    : miscImagesLoadFailed
                      ? 'Images unavailable'
                  : miscImages.length >= MAX_MISC_IMAGES
                    ? '10-image limit reached'
                    : 'Add images'}
              </label>
              <p className="site-change-images-help">
                {miscImages.length >= MAX_MISC_IMAGES
                  ? 'Delete an image before adding another.'
                  : `You can add ${MAX_MISC_IMAGES - miscImages.length} more image${MAX_MISC_IMAGES - miscImages.length === 1 ? '' : 's'}.`}
              </p>
            </section>

            <div className="modal-buttons">
              <button
                onClick={submitModifyRequest}
                className="submit-issue-btn"
                disabled={miscImagesUploading}
              >
                Submit Request
              </button>
              <button
                onClick={() => setShowModifyModal(false)}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showModifySuccessModal && (
        <div className="modal-overlay" onClick={() => setShowModifySuccessModal(false)}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✓</div>
            <h3>Change Request Submitted</h3>
            <p>Thanks! Your request is in our queue. Most changes are completed within 1-2 business days.</p>
            <div className="modal-buttons">
              <button
                onClick={() => setShowModifySuccessModal(false)}
                className="success-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showReportModal && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Report an Issue</h3>
            <p>Please describe the issue you're experiencing:</p>
            
            <label>Issue Type:</label>
            <select
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              className="issue-select"
            >
              <option value="Site Issue">Site Issue</option>
              <option value="Site Down">Site Down</option>
              <option value="Performance Issue">Performance Issue</option>
              <option value="Feature Request">Feature Request</option>
              <option value="Bug Report">Bug Report</option>
              <option value="Other">Other</option>
            </select>
            
            <label>Description:</label>
            <textarea
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              placeholder="Please provide details about the issue..."
              className="issue-textarea"
              rows="4"
            />
            
            <div className="modal-buttons">
              <button onClick={submitIssueReport} className="submit-issue-btn">
                Submit Report
              </button>
              <button 
                onClick={() => {
                  setShowReportModal(false);
                  setIssueType('Site Issue');
                  setIssueDescription('');
                }}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showIssueSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowIssueSuccessModal(false)}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✓</div>
            <h3>Issue Reported Successfully</h3>
            <p>Thank you for reporting the issue. Our team has been notified and will investigate promptly.</p>
            <div className="modal-buttons">
              <button 
                onClick={() => setShowIssueSuccessModal(false)}
                className="success-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <button 
        className="add-private-listing-btn"
        onClick={() => setShowPrivateListingForm(true)}
      >
        + Add Private Listing
      </button>

      <PrivateListingForm 
        show={showPrivateListingForm}
        onClose={() => setShowPrivateListingForm(false)}
        onSuccess={() => {
          setShowPrivateListingForm(false);
          setShowListingSuccessModal(true);
        }}
      />

      {showListingSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowListingSuccessModal(false)}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✓</div>
            <h3>Listing Added Successfully</h3>
            <p>Your private listing has been added to the system.</p>
            <div className="modal-buttons">
              <button onClick={() => setShowListingSuccessModal(false)} className="success-close-btn">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
